import React, { useEffect, useState } from 'react';
import HelpButton from '../Admin/HelpButton';

interface SEOSettings {
  defaults: {
    title?: string;
    description?: string;
    image?: string;
    author?: string;
    keywords?: string[];
    noIndex?: boolean;
    canonicalUrl?: string;
  };
  perPage: Record<string, {
    title?: string;
    description?: string;
    image?: string;
    author?: string;
    keywords?: string[];
    noIndex?: boolean;
    canonicalUrl?: string;
  }>;
}

const DEFAULTS: SEOSettings = {
  defaults: {
    title: 'Carmen Lorraine Photography - Professional Photography Services',
    description: 'Professional photography services specializing in portraits, weddings, and events.',
    image: '/logo512.png',
    author: 'Carmen Lorraine',
    keywords: ['photography', 'portraits', 'weddings', 'events', 'professional photographer'],
    noIndex: false,
  },
  perPage: {}
};

const STORAGE_KEY = 'seo_settings';

const SEOSettingsForm: React.FC = () => {
  const [settings, setSettings] = useState<SEOSettings>(DEFAULTS);
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [usePageOverride, setUsePageOverride] = useState<boolean>(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setSettings({ ...DEFAULTS, ...parsed, defaults: { ...DEFAULTS.defaults, ...(parsed.defaults || {}) }, perPage: parsed.perPage || {} });
      }
    } catch (e) {
      // ignore
    }
    setCurrentPath(window.location.pathname || '/');
  }, []);

  const saveSettings = (next: SEOSettings) => {
    setSettings(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // ignore
    }
    // Let the site know SEO settings changed
    window.dispatchEvent(new CustomEvent('seo_settings_updated', { detail: next }));
  };

  const updateDefault = (key: keyof SEOSettings['defaults'], value: any) => {
    const next = { ...settings, defaults: { ...settings.defaults, [key]: value } };
    saveSettings(next);
  };

  const updatePage = (path: string, key: keyof SEOSettings['defaults'], value: any) => {
    const page = settings.perPage[path] || {};
    const next = { ...settings, perPage: { ...settings.perPage, [path]: { ...page, [key]: value } } };
    saveSettings(next);
  };

  const resetToDefaults = () => {
    saveSettings(DEFAULTS);
  };

  const applyPortraitPreset = () => {
    updateDefault('title', 'Cape Town Portrait Photographer | Carmen Lorraine Photography');
    updateDefault('description', 'Professional portrait photography in Cape Town. Natural, elegant portraits for individuals, families, and professionals.');
    updateDefault('keywords', ['portrait photographer', 'Cape Town portraits', 'family photography', 'headshots']);
  };

  const applyWeddingPreset = () => {
    updateDefault('title', 'Cape Town Wedding Photographer | Carmen Lorraine Photography');
    updateDefault('description', 'Artistic wedding photography in Cape Town and surrounds. Timeless storytelling with a relaxed, natural approach.');
    updateDefault('keywords', ['wedding photographer', 'Cape Town weddings', 'elopement photography', 'bridal portraits']);
  };

  const enableOverride = (enabled: boolean) => {
    setUsePageOverride(enabled);
    if (enabled && !settings.perPage[currentPath]) {
      const next = { ...settings, perPage: { ...settings.perPage, [currentPath]: {} } };
      saveSettings(next);
    }
  };

  const applyDefaultsToPage = () => {
    const nextPage = { ...settings.defaults };
    const next = { ...settings, perPage: { ...settings.perPage, [currentPath]: nextPage } };
    saveSettings(next);
    setUsePageOverride(true);
  };

  const page = settings.perPage[currentPath] || {};

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0 }}>Global SEO Defaults</h4>
        <HelpButton section="seo-settings" tooltip="SEO Settings Help" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Default Title</label>
          <input
            type="text"
            value={settings.defaults.title || ''}
            onChange={(e) => updateDefault('title', e.target.value)}
            placeholder="Site-wide default title"
          />
        </div>
        <div className="form-group">
          <label>Default Author</label>
          <input
            type="text"
            value={settings.defaults.author || ''}
            onChange={(e) => updateDefault('author', e.target.value)}
            placeholder="Author/Brand"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Default Description</label>
        <textarea
          rows={3}
          value={settings.defaults.description || ''}
          onChange={(e) => updateDefault('description', e.target.value)}
          placeholder="Site-wide meta description"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Default Social Image URL</label>
          <input
            type="text"
            value={settings.defaults.image || ''}
            onChange={(e) => updateDefault('image', e.target.value)}
            placeholder="/path-or-https://..."
          />
        </div>
        <div className="form-group">
          <label>Default Keywords (comma separated)</label>
          <input
            type="text"
            value={(settings.defaults.keywords || []).join(', ')}
            onChange={(e) => updateDefault('keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="weddings, portraits, events"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={!!settings.defaults.noIndex}
              onChange={(e) => updateDefault('noIndex', e.target.checked)}
            />
            Noindex entire site
          </label>
        </div>
        <div className="form-group">
          <label>Canonical URL (optional)</label>
          <input
            type="text"
            value={settings.defaults.canonicalUrl || ''}
            onChange={(e) => updateDefault('canonicalUrl', e.target.value)}
            placeholder="https://www.example.com/"
          />
        </div>
      </div>

      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button className="btn btn-secondary" onClick={resetToDefaults}><i className="fas fa-undo"></i> Reset to Defaults</button>
        <button className="btn btn-primary" onClick={applyPortraitPreset}><i className="fas fa-user"></i> Portraits Preset</button>
        <button className="btn btn-primary" onClick={applyWeddingPreset}><i className="fas fa-heart"></i> Weddings Preset</button>
      </div>

      <hr style={{ margin: '1.5rem 0' }} />

      <h4>Per-Page Override</h4>
      
      {/* Visual Page Selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Select Page</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
          {[
            { path: '/', name: 'Home', icon: 'fas fa-home' },
            { path: '/galleries', name: 'Galleries', icon: 'fas fa-images' },
            { path: '/blog', name: 'Blog', icon: 'fas fa-blog' },
            { path: '/services', name: 'Services', icon: 'fas fa-gift' },
            { path: '/about', name: 'About', icon: 'fas fa-user' },
            { path: '/contact', name: 'Contact', icon: 'fas fa-envelope' }
          ].map(page => {
            const pageSettings = settings.perPage?.[page.path] || {};
            const hasOverride = Object.keys(pageSettings).length > 0;
            return (
              <button
                key={page.path}
                type="button"
                onClick={() => {
                  setCurrentPath(page.path);
                  if (hasOverride) {
                    setUsePageOverride(true);
                  }
                }}
                style={{
                  padding: '0.75rem',
                  border: `2px solid ${currentPath === page.path ? '#8B4513' : hasOverride ? '#16a34a' : '#e5e7eb'}`,
                  borderRadius: '6px',
                  background: currentPath === page.path ? '#fef9f3' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                  transition: 'all 0.2s'
                }}
              >
                <i className={page.icon} style={{ fontSize: '1.25rem', color: currentPath === page.path ? '#8B4513' : '#6b7280' }}></i>
                <span style={{ fontSize: '0.875rem', fontWeight: currentPath === page.path ? 600 : 400 }}>
                  {page.name}
                </span>
                {hasOverride && (
                  <i className="fas fa-check-circle" style={{ fontSize: '0.75rem', color: '#16a34a' }}></i>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Page Path (or enter custom path)</label>
          <input
            type="text"
            value={currentPath}
            onChange={(e) => setCurrentPath(e.target.value || '/')}
            placeholder="/about, /galleries, /blog/my-post"
          />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label>
            <input
              type="checkbox"
              checked={usePageOverride}
              onChange={(e) => enableOverride(e.target.checked)}
            />
            Enable override for this page
          </label>
        </div>
      </div>

      {usePageOverride && (
        <>
          <div className="form-actions" style={{ marginBottom: '1rem' }}>
            <button className="btn btn-secondary" onClick={applyDefaultsToPage}><i className="fas fa-copy"></i> Copy Defaults Into Page</button>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Page Title</label>
              <input
                type="text"
                value={page.title || ''}
                onChange={(e) => updatePage(currentPath, 'title', e.target.value)}
                placeholder="Custom page title"
              />
            </div>
            <div className="form-group">
              <label>Page Author</label>
              <input
                type="text"
                value={page.author || ''}
                onChange={(e) => updatePage(currentPath, 'author', e.target.value)}
                placeholder="Author/Brand"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Page Description</label>
            <textarea
              rows={3}
              value={page.description || ''}
              onChange={(e) => updatePage(currentPath, 'description', e.target.value)}
              placeholder="Custom meta description"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Page Social Image URL</label>
              <input
                type="text"
                value={page.image || ''}
                onChange={(e) => updatePage(currentPath, 'image', e.target.value)}
                placeholder="/path-or-https://..."
              />
            </div>
            <div className="form-group">
              <label>Page Keywords (comma separated)</label>
              <input
                type="text"
                value={(page.keywords || []).join(', ')}
                onChange={(e) => updatePage(currentPath, 'keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="weddings, portraits, events"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!page.noIndex}
                  onChange={(e) => updatePage(currentPath, 'noIndex', e.target.checked)}
                />
                Noindex this page
              </label>
            </div>
            <div className="form-group">
              <label>Canonical URL</label>
              <input
                type="text"
                value={page.canonicalUrl || ''}
                onChange={(e) => updatePage(currentPath, 'canonicalUrl', e.target.value)}
                placeholder="https://www.example.com/services"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SEOSettingsForm;


